import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";

export const buyerStrategySectionMeta = {
  componentId: "section-buyer-strategy-v1",
  displayName: "Buyer Strategy Section",
  category: "Sections",
} as const;

export interface BuyerStrategyFeature {
  title: string;
  description: string;
  icon?: LucideIcon;
}

interface BuyerStrategySectionProps {
  heading: string;
  subheading?: string;
  description: string;
  image: string;
  imageAlt?: string;
  cta: {
    label: string;
    href: string;
  };
  secondaryCta?: {
    label: string;
    href: string;
  };
  features: BuyerStrategyFeature[];
  reverse?: boolean;
  className?: string;
}

const BuyerStrategySection = ({
  heading,
  subheading,
  description,
  image,
  imageAlt = "",
  cta,
  secondaryCta,
  features,
  reverse = false,
  className = "",
}: BuyerStrategySectionProps) => {
  const imageBlock = (
    <div className="relative">
      <div className="relative overflow-hidden min-h-[420px] md:min-h-[560px]">
        <img
          src={image}
          alt={imageAlt}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary/36 via-transparent to-transparent" />
      </div>
      <div className="relative mx-5 -mt-14 md:mx-8 md:-mt-20 lg:ml-auto lg:mr-8 max-w-[320px]">
        <div className="bg-card border border-border shadow-[0_22px_48px_-24px_rgba(0,0,0,0.22)] p-6 md:p-7">
          <p className="font-body text-xs uppercase tracking-[0.14em] text-accent mb-3">
            Strategic Guidance
          </p>
          <h3 className="font-headline text-2xl text-foreground leading-tight mb-3">
            A more thoughtful way to move.
          </h3>
          <p className="text-muted-foreground text-base leading-relaxed">
            A composed, high-trust layout that keeps the visual strong while protecting readability.
          </p>
        </div>
      </div>
    </div>
  );

  const textBlock = (
    <div className="space-y-6">
      {subheading && (
        <span className="font-body text-xs uppercase tracking-[0.14em] text-accent">
          {subheading}
        </span>
      )}
      <h2 className="font-headline text-3xl md:text-5xl text-foreground leading-tight">
        {heading}
      </h2>
      <p className="text-muted-foreground text-base md:text-lg leading-[1.75] max-w-2xl">
        {description}
      </p>
      <div className="flex flex-col sm:flex-row gap-4 pt-2">
        <Link
          to={cta.href}
          className="inline-flex items-center justify-center gap-2 font-body text-sm uppercase tracking-[0.12em] px-8 py-4 bg-accent text-accent-foreground hover:bg-accent/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {cta.label}
        </Link>
        {secondaryCta && (
          <Link
            to={secondaryCta.href}
            className="inline-flex items-center justify-center gap-2 font-body text-sm uppercase tracking-[0.12em] px-8 py-4 border-2 border-foreground/35 text-foreground hover:border-accent hover:text-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {secondaryCta.label}
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <section
      aria-labelledby={buyerStrategySectionMeta.componentId}
      data-component-id={buyerStrategySectionMeta.componentId}
      className={`py-24 md:py-32 bg-background ${className}`}
    >
      <div className="mx-auto max-w-wide px-5 md:px-12">
        <div
          className={`grid grid-cols-1 lg:grid-cols-[1.02fr_0.98fr] gap-10 md:gap-16 items-start ${
            reverse ? "lg:[&>*:first-child]:order-2" : ""
          }`}
        >
          {imageBlock}
          <div id={buyerStrategySectionMeta.componentId}>{textBlock}</div>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
          {features.map((item) => (
            <div
              key={item.title}
              className="bg-card border border-border shadow-sm p-5 md:p-6"
            >
              {item.icon && (
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/12 border border-accent/20 mb-4">
                  <item.icon className="w-4 h-4 text-accent" strokeWidth={1.8} aria-hidden="true" />
                </div>
              )}
              <h3 className="font-headline text-xl text-foreground mb-2">{item.title}</h3>
              <p className="text-muted-foreground text-base leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BuyerStrategySection;
